import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { userlogin, validateToken, logout, performLogout, mfaSetup, mfaVerify } from '../redux/authSlice';
import { getLanguages } from '../redux/translationSlice';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useAxiosAuth from '../hooks/useAxiosAuth';
import { isSessionMismatch } from '../utils/sessionGuard';
import { useTranslation } from 'react-i18next';
import logo from '../assets/img/logo-TT2.png';
import { DollarSign, Lock, User, Eye, EyeOff, Heart, Stethoscope, Languages, ArrowLeft, ShieldCheck, KeyRound } from 'lucide-react';
import QRCode from 'qrcode';
import { initializeFCMEarly } from '../utils/initializeFCM';
import { firstAllowedRoute } from '../utils/platformPermissions';
import { isQueueScopingOn } from '../redux/featureSlice';
export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const languagePopupRef = useRef(null);
  const { i18n, t } = useTranslation('login');
  const axiosInstance = useAxiosAuth();
  const { user, isLoading, error } = useSelector((state) => state.auth);
  // Does a platform agent still need support.queue.<queue> to open a queue?
  // From the store on purpose: the logout localStorage.clear() has just wiped
  // the flag mirror, so on a same-tab re-login the mirror would say "ON" (its
  // default) even when the Super Admin switched queue scoping off.
  const queueScopingOn = useSelector(isQueueScopingOn);
  const languages = useSelector((state) => state.translation?.languages || []);

  useEffect(() => {
    if (!languages || languages.length === 0) {
      dispatch(getLanguages({ page: 1, limit: 100, search: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [isLanguagePopupOpen, setIsLanguagePopupOpen] = useState(false);
  const [isChecked, setIsChecked] = useState(i18n.language === 'fr');
  const [showPassword, setShowPassword] = useState(false);

  // SECOND FACTOR (infra review, 26 Sep 2026). A super admin's password is
  // only half the door: /auth/login pauses with an mfaToken, and this panel
  // finishes the sign-in — enrolling an authenticator app on the first visit
  // (QR + secret from /auth/mfa/setup), then a six-digit code every time
  // (/auth/mfa/verify), which returns the very session login would have.
  const [mfa, setMfa] = useState(null); // { stage: 'setup'|'verify', token, secret, otpauthUri, qr }
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [mfaBusy, setMfaBusy] = useState(false);

  const beginMfa = async ({ mfa: stage, mfaToken }) => {
    setMfaCode(''); setMfaError('');
    if (stage !== 'setup') { setMfa({ stage: 'verify', token: mfaToken }); return; }
    try {
      setMfaBusy(true);
      const { secret, otpauthUri } = await dispatch(mfaSetup({ mfaToken, axiosInstance })).unwrap();
      let qr = null;
      try { qr = await QRCode.toDataURL(otpauthUri, { margin: 1, width: 200 }); } catch { qr = null; }
      setMfa({ stage: 'setup', token: mfaToken, secret, otpauthUri, qr });
    } catch (err) {
      toast.error(err?.message || t('login.mfa_setup_failed', 'Could not start the authenticator setup. Please sign in again.'));
      setMfa(null);
    } finally {
      setMfaBusy(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    const code = mfaCode.replace(/\s+/g, '');
    if (!/^\d{6}$/.test(code)) {
      setMfaError(t('login.mfa_code_six_digits', 'Enter the six-digit code from your authenticator app.'));
      return;
    }
    try {
      setMfaBusy(true); setMfaError('');
      await dispatch(mfaVerify({ mfaToken: mfa.token, code, axiosInstance })).unwrap();
      // The fulfilled reducer sets `user`; the post-login effect above navigates.
    } catch (err) {
      if (err?.code === 'MFA_TOKEN_INVALID' || err?.code === 'MFA_NOT_ALLOWED') {
        toast.error(err.message || t('login.mfa_expired', 'Your sign-in has expired — start again.'));
        setMfa(null);
        return;
      }
      setMfaError(err?.message || t('login.mfa_code_wrong', 'That code is not right. Codes change every 30 seconds — try the current one.'));
    } finally {
      setMfaBusy(false);
    }
  };

  // A render FUNCTION, not a nested component: a component defined inside
  // the page would be a new type on every render, remounting the panel and
  // dropping the code input's focus on each keystroke.
  const renderMfaPanel = () => (
    <form onSubmit={handleMfaVerify} className="w-full" data-testid="mfa-panel">
      <h2 data-testid="mfa-title" className="text-lg sm:text-xl md:text-2xl font-bold text-center mb-2 text-[#4ABDBD]">
        {mfa.stage === 'setup'
          ? t('login.mfa_setup_title', 'Set up your authenticator')
          : t('login.mfa_verify_title', 'Two-step verification')}
      </h2>
      <p className="text-center text-gray-600 text-xs sm:text-sm mb-4">
        {mfa.stage === 'setup'
          ? t('login.mfa_setup_hint', 'Scan this QR code with Google Authenticator or Microsoft Authenticator, then enter the six-digit code it shows.')
          : t('login.mfa_verify_hint', 'Enter the six-digit code from your authenticator app.')}
      </p>
      {mfa.stage === 'setup' && (
        <div className="flex flex-col items-center gap-2 mb-4">
          {mfa.qr ? (
            <img data-testid="mfa-qr" src={mfa.qr} alt="" width={200} height={200} className="rounded-xl border border-gray-200 bg-white p-1" />
          ) : null}
          <p className="text-[11px] text-gray-500">{t('login.mfa_manual', "Can't scan? Enter this key manually:")}</p>
          <code data-testid="mfa-secret" className="font-mono text-xs sm:text-sm tracking-widest bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 select-all">
            {mfa.secret}
          </code>
        </div>
      )}
      <div
        data-testid="mfa-code-container"
        className={`relative flex items-center border rounded-lg sm:rounded-xl px-2 sm:px-3 md:px-4 py-2 sm:py-3 mb-2 bg-white/80 backdrop-blur-sm focus-within:ring-2 focus-within:bg-white transition-all duration-300 shadow-sm ${mfaError ? 'border-red-400 focus-within:ring-red-400' : 'border-gray-200 focus-within:ring-[#4ABDBD] focus-within:border-[#4ABDBD]'}`}
      >
        <KeyRound className="mr-2 sm:mr-2.5 md:mr-3 text-[#4ABDBD] w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
        <input
          data-testid="mfa-code-input"
          name="mfaCode"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          maxLength={7}
          value={mfaCode}
          onChange={(e) => { setMfaCode(e.target.value.replace(/[^\d\s]/g, '')); setMfaError(''); }}
          placeholder="123 456"
          className="w-full bg-transparent focus:outline-none text-gray-800 text-base tracking-widest font-mono"
          disabled={mfaBusy}
        />
      </div>
      {mfaError && <p data-testid="mfa-error" className="text-red-500 text-xs sm:text-sm mb-2">{mfaError}</p>}
      <button
        type="submit"
        data-testid="mfa-submit-button"
        disabled={mfaBusy}
        className={`w-full py-2.5 sm:py-3 font-bold rounded-lg sm:rounded-xl text-white bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] hover:from-[#4ABDBD]/90 hover:to-[#3A9B9B]/90 transition-all duration-300 flex items-center justify-center gap-2 text-sm sm:text-base shadow-lg ${mfaBusy ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:shadow-xl'}`}
      >
        <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5" />
        {mfa.stage === 'setup' ? t('login.mfa_confirm', 'Confirm and sign in') : t('login.mfa_verify', 'Verify')}
      </button>
      <button
        type="button"
        data-testid="mfa-cancel"
        onClick={() => { setMfa(null); setMfaCode(''); setMfaError(''); }}
        className="mt-3 w-full text-xs sm:text-sm text-gray-500 hover:text-[#2D8A8A] cursor-pointer"
      >
        {t('login.mfa_start_over', 'Start over')}
      </button>
    </form>
  );
  const [formData, setFormData] = useState({ email: '', password: '' });
  // No organisation picker here — this portal has exactly one account type
  // (super_admin), which is tenant-agnostic by design (Backend/api/
  // controllers/authController.js: "super_admin spans every tenant, so it
  // matches any"). The per-tenant selector, subdomain lock and login-
  // identifier-mode machinery all belong to the tenant app's Login.jsx this
  // page was copied from; a super admin always signs in with email.
  const loginIdentifierMode = 'email';
  const [errors, setErrors] = useState({ email: '', password: '' });

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  // Validate email format
  const validateEmail = (email) => {
    if (!email) {
      return t('login.email_required');
    }
    
    // Sanitize input - remove dangerous characters
    const sanitized = email.replace(/[<>]/g, '');
    
    // Check for whitespace-only
    if (sanitized.trim().length === 0 && sanitized.length > 0) {
      return t('login.email_required');
    }
    
    // Check max length (254 characters is standard email max length)
    if (sanitized.length > 254) {
      return t('login.email_too_long') || 'Email cannot exceed 254 characters';
    }
    
    // Check local part (before @) max 64 characters
    const localPart = sanitized.split('@')[0];
    if (localPart && localPart.length > 64) {
      return t('login.email_local_part_too_long');
    }
    
    // Check domain part (after @) max 253 characters
    const domainPart = sanitized.split('@')[1];
    if (domainPart && domainPart.length > 253) {
      return t('login.email_domain_part_too_long');
    }
    
    // Check email format
    if (!emailRegex.test(sanitized)) {
      return t('login.invalid_email_format');
    }

    return '';
  };

  // Validate a mobile number: 6–15 digits once formatting is stripped (covers
  // every national numbering plan; numbers are stored without country code).
  const validateMobile = (value) => {
    const digits = value.replace(/[^0-9]/g, '');
    if (!digits) {
      return t('login.mobile_required', 'Mobile number is required');
    }
    if (digits.length < 6 || digits.length > 15) {
      return t('login.invalid_mobile_format', 'Enter a valid mobile number (6–15 digits)');
    }
    return '';
  };

  // Mode-aware identifier validation. An '@' anywhere means the person is
  // typing an email — accepted even in 'mobile' mode (the backend only honours
  // it for the super admin; hinting at that here would advertise the
  // break-glass, so the field stays phone-worded).
  const validateIdentifier = (value) => {
    if (!value) {
      if (loginIdentifierMode === 'mobile') return t('login.mobile_required', 'Mobile number is required');
      if (loginIdentifierMode === 'both') return t('login.identifier_required', 'Email or mobile number is required');
      return t('login.email_required');
    }
    if (loginIdentifierMode === 'email' || value.includes('@')) return validateEmail(value);
    return validateMobile(value);
  };

  // Validate password
  const validatePassword = (password) => {
    if (!password) {
      return t('login.password_required');
    }
    
    // Sanitize input - remove dangerous characters
    const sanitized = password.replace(/[<>]/g, '');
    
    // Check for whitespace-only
    if (sanitized.trim().length === 0 && sanitized.length > 0) {
      return t('login.password_required');
    }
    
    // Check min length
    if (sanitized.length < 1) {
      return t('login.password_required');
    }
    
    // Check max length (reasonable limit for password)
    if (sanitized.length > 100) {
      return t('login.password_too_long');
    }
    
    return '';
  };

  // Initialize FCM early (before login)
  useEffect(() => {
    // Initialize FCM as soon as Login page loads
    initializeFCMEarly();
  }, []);

  // Redirect to dashboard when this tab can use an existing session.
  //
  // Two cases:
  //   1. This tab just logged in (Redux `user` got populated) — go to the
  //      role's dashboard.
  //   2. A fresh tab opened /login while another tab in the same browser is
  //      already signed in. Redux `user` is null in this tab, but localStorage
  //      shows an active session — pick the role from there.
  //
  // Skip both cases when the tab is exiled (the cross-tab guard kicked us out
  // because another tab signed in as a different user). Otherwise this effect
  // would ping-pong with PrivateRoute: redirect → mismatch → back to /login.
  useEffect(() => {
    if (isSessionMismatch()) return;

    const role = user?.role || localStorage.getItem('Role');
    const isAuthed = localStorage.getItem('isAuthenticated') === 'true';
    const id = localStorage.getItem('ID');
    if (!isAuthed || !role || !id) return;

    // SUPER ADMIN PORTAL. The super admin and their own PLATFORM STAFF (support
    // agents etc.) belong here — any other role that signs in successfully is
    // told so and signed straight out, so tenant users go back to their
    // hospital's own application.
    const isPlatformStaff = role === 'platform_staff';
    if (localStorage.getItem('isSuperAdmin') !== 'true' && !isPlatformStaff) {
      toast.error(
        t('login.superadmin_portal_only', "This is the Super Admin portal. Please sign in from your hospital's application."),
        { autoClose: 6000 }
      );
      dispatch(performLogout({ axiosInstance }));
      return;
    }

    // Super Admin's home is the platform dashboard. Platform staff go straight to
    // the first page their permissions allow (a support queue when held, else
    // the first sidebar entry); "/" (App.jsx's DefaultLanding) is the fallback
    // that shows "no access" when none.
    navigate(isPlatformStaff ? (firstAllowedRoute(queueScopingOn) || '/') : '/dashboard', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const handleToggleChange = () => {
    const newLanguage = isChecked ? 'en' : 'fr';
    i18n.changeLanguage(newLanguage);
    setIsChecked(!isChecked);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languagePopupRef.current && !languagePopupRef.current.contains(event.target)) {
        setIsLanguagePopupOpen(false);
      }
    };
    if (isLanguagePopupOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLanguagePopupOpen]);

  // Handle input changes with validation
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Sanitize input - remove dangerous characters
    const sanitized = value.replace(/[<>]/g, '');
    
    // Enforce max length for email
    let finalValue = sanitized;
    if (name === 'email' && sanitized.length > 254) {
      finalValue = sanitized.substring(0, 254);
    }
    // Enforce max length for password
    if (name === 'password' && sanitized.length > 100) {
      finalValue = sanitized.substring(0, 100);
    }
    
    setFormData((prev) => ({ ...prev, [name]: finalValue }));

    // Validate the identifier (email or mobile, per the platform mode)
    if (name === 'email') {
      const error = validateIdentifier(finalValue);
      setErrors((prev) => ({ ...prev, email: error }));
    }

    // Validate password
    if (name === 'password') {
      const error = validatePassword(finalValue);
      setErrors((prev) => ({ ...prev, password: error }));
    }
  };

  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
    setIsLanguagePopupOpen(false);
  };

  // Handle login submission
  const handleLogin = async (e) => {
    e.preventDefault();

    // Final validation before submission
    const emailError = validateIdentifier(formData.email);
    const passwordError = validatePassword(formData.password);
    
    if (emailError || passwordError) {
      setErrors({ email: emailError, password: passwordError });
      toast.warning(t('login.form_errors') || 'Please fix the errors in the form', { autoClose: 2000 });
      return;
    }
    
    if (!formData.email || !formData.password) {
      toast.warning(t('login.fill_all_fields'), { autoClose: 2000 });
      return;
    }

    try {
      // `identifier` carries the email; the super admin identity is
      // tenant-agnostic, so no tenantId is sent (Backend resolves it as a
      // single global row — there is never a hospital to disambiguate).
      await dispatch(userlogin({
        formdata: {
          identifier: formData.email.trim(),
          password: formData.password,
          tenantId: null,
        },
        axiosInstance,
      })).unwrap();
    } catch (err) {
      // The password was right; the second factor decides (super admin).
      if (err && err.mfa) {
        await beginMfa(err);
        return;
      }
      // Bulk-uploaded patients must verify their email + set a new password
      // before they can sign in — route them into the activation flow. Kept
      // for completeness even though this portal's own accounts never hit it.
      if (err && err.verificationRequired) {
        navigate('/activate', { state: { email: err.email || null, phone: err.phone || null, tenantId: err.tenantId || null } });
        return;
      }
      // Other errors (invalid credentials, etc.) are handled in useEffect and useAxiosAuth
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen relative w-full flex flex-col items-center justify-center px-2 sm:px-4 md:px-6 lg:px-8 bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B]/90 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -right-20 sm:-top-40 sm:-right-40 w-40 h-40 sm:w-80 sm:h-80 bg-white/20 rounded-full mix-blend-multiply filter blur-xl opacity-60 animate-blob"></div>
        <div className="absolute -bottom-20 -left-20 sm:-bottom-40 sm:-left-40 w-40 h-40 sm:w-80 sm:h-80 bg-gray-200/30 rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-blob animation-delay-2000"></div>
        <div className="absolute top-20 left-20 sm:top-40 sm:left-40 w-40 h-40 sm:w-80 sm:h-80 bg-white/10 rounded-full mix-blend-multiply filter blur-xl opacity-40 animate-blob animation-delay-4000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 sm:w-96 sm:h-96 bg-white/5 rounded-full mix-blend-multiply filter blur-2xl opacity-30 animate-blob animation-delay-1000"></div>
      </div>

      {/* Back Button */}
      <div className="absolute top-4 left-4 sm:top-6 sm:left-6 z-50">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 p-1.5 text-white focus:outline-none cursor-pointer bg-white/20 backdrop-blur-sm border border-white/30 shadow-lg rounded-2xl hover:bg-white/30 transition-all duration-300"
          aria-label={t('login.back_button')}
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
      </div>

      {/* Language Switcher */}
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-50">
        <button
          data-testid="language-toggle-button"
          onClick={() => setIsLanguagePopupOpen(!isLanguagePopupOpen)}
          className="flex items-center gap-2 p-1.5 text-white focus:outline-none cursor-pointer bg-white/20 backdrop-blur-sm border border-white/30 shadow-lg rounded-2xl"
          aria-label={t('login.select_language')}
        >
          <Languages className="h-6 w-6" />
        </button>
        {isLanguagePopupOpen && (
          <div
            data-testid="language-popup"
            ref={languagePopupRef}
            className="absolute right-0 top-full mt-2 w-44 max-h-72 overflow-y-auto bg-white rounded-lg shadow-lg p-2 z-40 border border-gray-200"
          >
            {languages && languages.length > 0 ? (
              languages.map((langItem) => {
                const code = (langItem.code || '').toLowerCase();
                const active = i18n.language === code;
                return (
                  <button
                    key={langItem.languageId || code}
                    data-testid={`language-option-${code}`}
                    onClick={() => handleLanguageChange(code)}
                    className={`w-full text-left mb-1 last:mb-0 px-4 cursor-pointer py-2 text-sm rounded-md ${active
                      ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate">{langItem.name}</span>
                      <span className={`text-[10px] font-bold uppercase ${active ? 'text-white/80' : 'text-gray-400'}`}>{code}</span>
                    </span>
                  </button>
                );
              })
            ) : (
              <button
                data-testid="english-option"
                onClick={() => handleLanguageChange('en')}
                className={`w-full text-left px-4 cursor-pointer py-2 text-sm rounded-md ${i18n.language === 'en'
                  ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white'
                  : 'text-gray-700 hover:bg-gray-100'
                  }`}
              >
                {t('login.language_english')}
              </button>
            )}
          </div>
        )}
      </div>


      {/* Custom Styles */}
      <style>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes typewriter {
          from {
            width: 0;
          }
          to {
            width: 100%;
          }
        }
        @keyframes blink {
          0%, 50% {
            border-color: transparent;
          }
          51%, 100% {
            border-color: #4ABDBD;
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-pulse-slow {
          animation: pulse 3s ease-in-out infinite;
        }
        .animate-slideInUp {
          animation: slideInUp 0.8s ease-out;
        }
        .animate-slideInLeft {
          animation: slideInLeft 0.8s ease-out;
        }
        .animate-slideInRight {
          animation: slideInRight 0.8s ease-out;
        }
        .animate-typewriter {
          animation: typewriter 2s steps(40, end);
        }
        .animate-blink {
          animation: blink 1s infinite;
        }
        .text-gradient {
          background: linear-gradient(135deg, #4ABDBD 0%, #3A9B9B 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .glass-effect {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
      `}</style>

      {/* Main Container */}
      <div className="w-full max-w-7xl flex flex-col lg:flex-row items-center justify-center gap-4 sm:gap-6 md:gap-8 lg:gap-12 relative z-10 py-4 sm:py-8">

        {/* Left Section - Branding */}
        <div className="flex flex-col items-center justify-center gap-4 sm:gap-6 md:gap-8 w-full lg:w-1/2 animate-slideInLeft px-4 sm:px-0">
          {/* Animated Logo */}
          <div className="relative">
            <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 bg-white rounded-full flex items-center justify-center animate-float shadow-2xl border border-white/30">
              <img src={logo} className="w-20 h-20" />
            </div>
            <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 w-4 h-4 sm:w-6 sm:h-6 md:w-8 md:h-8 bg-white/40 rounded-full animate-ping"></div>
            <div className="absolute -bottom-1 -left-1 sm:-bottom-2 sm:-left-2 w-3 h-3 sm:w-4 sm:h-4 md:w-6 md:h-6 bg-white/30 rounded-full animate-ping animation-delay-2000"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 sm:w-32 sm:h-32 md:w-36 md:h-36 lg:w-40 lg:h-40 bg-white/5 rounded-full animate-pulse-slow"></div>
          </div>

          {/* Animated Title */}
          <div className="text-center px-2">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-2 sm:mb-4 drop-shadow-lg">
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.2s' }}>M</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.3s' }}>i</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.4s' }}>t</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.5s' }}>r</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.6s' }}>a</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.7s' }}>{' '}</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.8s' }}>O</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.9s' }}>n</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '1.0s' }}>e</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '1.1s' }}>{' '}</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '1.2s' }}>C</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '1.3s' }}>a</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '1.4s' }}>r</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '1.5s' }}>e</span>
            </h1>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 font-light animate-slideInUp drop-shadow-md" style={{ animationDelay: '1.6s' }}>
              {t('login.welcome_quote1')}
            </p>
          </div>

          {/* Floating Icons */}
          <div className="flex gap-4 sm:gap-6 md:gap-8 animate-slideInUp" style={{ animationDelay: '1.8s' }}>
            <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center animate-float border border-white/30 shadow-lg">
              <Stethoscope className="text-white text-lg sm:text-xl md:text-2xl" />
            </div>
            <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-white/15 backdrop-blur-sm rounded-full flex items-center justify-center animate-float border border-white/20 shadow-lg" style={{ animationDelay: '1s' }}>
              <Heart className="text-white text-lg sm:text-xl md:text-2xl" />
            </div>
            <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center animate-float border border-white/25 shadow-lg" style={{ animationDelay: '2s' }}>
              <Stethoscope className="text-white text-lg sm:text-xl md:text-2xl" />
            </div>
          </div>
        </div>

        {/* Right Section - Login Form */}
        <div data-testid="login-form-container" className="relative z-10 bg-white/95 backdrop-blur-md p-4 sm:p-6 md:p-8 lg:p-12 rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-sm sm:max-w-md flex flex-col gap-4 sm:gap-6 md:gap-8 animate-slideInRight border border-white/20 mx-2 sm:mx-0">
          {/* Welcome Quote */}
          <p data-testid="welcome-quote" className="text-center text-gray-700 text-sm sm:text-base md:text-lg font-medium italic animate-slideInUp" style={{ animationDelay: '0.5s' }}>
            "{t('login.welcome_quote')}"
          </p>

          {/* Main Content */}
          <div className="flex justify-center">
            {mfa ? (
              renderMfaPanel()
            ) : (
            <form onSubmit={handleLogin} className="w-full" data-testid="login-form">
              <h2
                data-testid="login-title"
                className="text-lg sm:text-xl md:text-2xl font-bold text-center mb-3 sm:mb-4 md:mb-6 text-[#4ABDBD]"
              >
                {t('login.sign_in_title')}
              </h2>

              {/* Email input — this portal has one account type (super_admin),
                  which is tenant-agnostic, so there is no organisation picker
                  and no mobile/both identifier mode here. */}
              <div
                data-testid="email-input-container"
                className={`relative flex items-center border rounded-lg sm:rounded-xl px-2 sm:px-3 md:px-4 py-2 sm:py-3 mb-3 sm:mb-4 ${errors.email ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white/80 backdrop-blur-sm'} focus-within:ring-2 focus-within:ring-[#4ABDBD] focus-within:bg-white focus-within:border-[#4ABDBD] transition-all duration-300 shadow-sm`}
              >
                <User
                  data-testid="email-icon"
                  className="mr-2 sm:mr-2.5 md:mr-3 text-[#4ABDBD] text-sm sm:text-base md:text-lg"
                />
                <input
                  data-testid="email-input"
                  name="email"
                  type="email"
                  placeholder={t('login.email_placeholder')}
                  value={formData.email}
                  onChange={handleInputChange}
                  maxLength={254}
                  className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-500 text-sm sm:text-sm md:text-base"
                  required
                  autoComplete="email"
                />
              </div>
              {errors.email && (
                <p data-testid="email-error" className="text-red-300 text-xs mb-3 -mt-1">
                  {errors.email}
                </p>
              )}

              {/* Password input */}
              <div
                data-testid="password-input-container"
                className={`relative flex items-center border rounded-lg sm:rounded-xl px-2 sm:px-3 md:px-4 py-2 sm:py-3 mb-3 sm:mb-4 ${errors.password ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white/80 backdrop-blur-sm'} focus-within:ring-2 focus-within:ring-[#4ABDBD] focus-within:bg-white focus-within:border-[#4ABDBD] transition-all duration-300 shadow-sm`}
              >
                <Lock
                  data-testid="password-icon"
                  className="mr-2 sm:mr-2.5 md:mr-3 text-[#4ABDBD] text-sm sm:text-base md:text-lg"
                />
                <input
                  data-testid="password-input"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('login.password_placeholder')}
                  value={formData.password}
                  onChange={handleInputChange}
                  maxLength={100}
                  className={`w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-500 text-sm sm:text-sm md:text-base`}
                  required
                  autoComplete="current-password"
                />
                <button
                  data-testid="toggle-password-visibility"
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="ml-2 sm:ml-2.5 md:ml-3 focus:outline-none hover:scale-105 transition-transform cursor-pointer"
                  aria-label={showPassword ? t('login.hide_password_aria') : t('login.show_password_aria')}
                >
                  {showPassword ? (
                    <Eye data-testid="show-password-icon" className="text-[#4ABDBD] text-sm sm:text-base md:text-lg" />
                  ) : (
                    <EyeOff data-testid="hide-password-icon" className="text-[#4ABDBD] text-sm sm:text-base md:text-lg" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p data-testid="password-error" className="text-red-300 text-xs mb-3 -mt-1">
                  {errors.password}
                </p>
              )}

              {/* Forgot Password Link */}
              <div className="flex justify-end mb-3 sm:mb-4 md:mb-6">
                <p
                  data-testid="forgot-password-link"
                  className="text-right text-xs sm:text-xs text-[#4ABDBD] cursor-pointer hover:text-[#3A9B9B] hover:underline transition-colors"
                  onClick={() => navigate('/forgot-password', { state: { email: formData.email, tenantId: null } })}
                >
                  {t('login.forgot_password')}
                </p>
              </div>

              {/* Sign In button */}
              <button
                data-testid="login-submit-button"
                type="submit"
                disabled={isLoading || errors.email || errors.password}
                className={`w-full py-2.5 sm:py-3 font-bold rounded-lg sm:rounded-xl text-white bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] hover:from-[#4ABDBD]/90 hover:to-[#3A9B9B]/90 transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 text-sm sm:text-sm md:text-base shadow-lg hover:shadow-xl transform hover:scale-105 ${isLoading || errors.email || errors.password ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'
                  }`}
              >
                {isLoading ? (
                  <span
                    data-testid="login-loading-spinner"
                    className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"
                  ></span>
                ) : (
                  <>
                    <Stethoscope className="text-sm sm:text-base md:text-lg" />
                    {t('login.sign_in_button')}
                  </>
                )}
              </button>
            </form>
            )}
          </div>

          {/* No register link — accounts on the Super Admin portal are never
              self-service; the tenant app owns registration. */}

          {/* Footer */}
          <footer data-testid="login-footer" className="text-center text-xs sm:text-sm text-gray-600 animate-slideInUp" style={{ animationDelay: '1.7s' }}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
              <span>
                {t('login.footer_copyright', { year: new Date().getFullYear() })}
              </span>
            </div>
            <span data-testid="powered-by" className="text-[#4ABDBD] font-medium">
              <span data-testid="app-version" className="text-[#4ABDBD] font-medium mr-3">
                {t('login.app_version')}
              </span>
              {t('login.powered_by')}
            </span>
          </footer>
        </div>
      </div>
    </div>
  );
}